<script setup lang="ts">
  import { onLongPress } from "@vueuse/core";
  import { useTalkRoomEditStore } from "~/stores/TalkRoomEditStore";
  import { useTalkRoomViewStore } from "~/stores/TalkRoomViewStore";
  import { useSnackBarStore } from "~/stores/SnackBarStore";
  import { TalkRoom } from "~/entities/TalkRoom";
  import { PlanInfo } from "~/entities/PlanInfo";
  import { useHistoryStore } from "~/stores/HistoryStore";

  const historyStore = useHistoryStore();

  interface Props {
    planInfo: PlanInfo;
    selectedTalkRoom: TalkRoom;
    isOwner: boolean;
    isSearching: boolean;
    backToListView: () => void;
  }
  const props = defineProps<Props>();

  interface Emits {
    (event: "update:isSearching", isSearching: boolean): void;
  }
  const emit = defineEmits<Emits>();

  const planId = props.planInfo.id;

  // 閲覧権限
  const canView = (talkRoom: TalkRoom | undefined) => {
    return canViewTalkRoom(talkRoom, props.isOwner, props.planInfo?.isContract);
  };

  // 閲覧権限
  const canViewSelectedRoom = computed<boolean>(() => {
    return canView(props.selectedTalkRoom);
  });

  const selectedRoomNameLabel = computed(() => {
    if (props.selectedTalkRoom.isDirectMessage) {
      return dMTalkRoomLabel(props.selectedTalkRoom, props.planInfo);
    }
    return props.selectedTalkRoom.roomName;
  });

  const selectedRoomDescriptionLabel = computed(() => {
    return props.selectedTalkRoom.isDirectMessage ? dMTalkRoomDescription() : props.selectedTalkRoom.description;
  });

  const selectedRoomIconSrc = computed(() => getTalkRoomIcon(props.selectedTalkRoom, props.planInfo));

  // 編集
  const talkRoomEditStore = useTalkRoomEditStore();
  const clickEditTalkRoom = () => {
    talkRoomEditStore.initBy(props.selectedTalkRoom);
    if (props.selectedTalkRoom.isDirectMessage) {
      historyStore.to(`/plan/${planId}/talk/${props.selectedTalkRoom.talkRoomId}/editdm`);
    } else {
      historyStore.to(`/plan/${planId}/talk/${props.selectedTalkRoom.talkRoomId}/edit`);
    }
  };

  // 検索
  const clickSearch = () => {
    emit("update:isSearching", !props.isSearching);
  };

  // 新規ウィンドウで開く
  const clickOpenInNew = () => {
    window.open(location.href, "_blank");
  };

  // トークルームタイトルに隠しコマンドを仕込む
  // - クリックで再読み込み。メッセージ非表示問題の応急処置用 https://arkad-co.slack.com/archives/C06P8P9L95G/p1724822419800429?thread_ts=1724560117.232529&cid=C06P8P9L95G
  // - 長押しで firestore cache ON/OFF トグル。デバッグ用 https://arkad-co.slack.com/archives/C06P8P9L95G/p1725238366852219?thread_ts=1725063683.929319&cid=C06P8P9L95G
  const talkRoomTitle = ref<HTMLElement | null>(null);
  const talkRoomViewStore = useTalkRoomViewStore();
  const snackBarStore = useSnackBarStore();
  const onPressTitleShort = () => {
    reloadNuxtApp({ ttl: 1000 }); // デフォルト 10,000
  };
  const onPressTitleLong = () => {
    talkRoomViewStore.isFirestoreCacheEnabled = !talkRoomViewStore.isFirestoreCacheEnabled;
    sentryInfoLog(`onLongPress: firestore cache ${talkRoomViewStore.isFirestoreCacheEnabled ? "ON" : "OFF"}`);
    snackBarStore.set(
      `トークメッセージのキャッシュを${talkRoomViewStore.isFirestoreCacheEnabled ? "有効" : "無効"}にしました`,
    );
  };
  onLongPress(talkRoomTitle, onPressTitleLong, {
    delay: 3000,
    onMouseUp: (_duration: number, _distance: number, isLongPress: boolean) => {
      if (!isLongPress) {
        onPressTitleShort();
      }
    },
  });

  // レスポンシブ対応
  const isTablet = isTabletAndSpDisplayRef();
</script>

<template>
  <div class="talkroom_title_wrapper d-flex">
    <template v-if="isTablet">
      <v-icon icon="$mdiChevronLeft" class="back-button-sp" @click="backToListView()"></v-icon>
      <div class="talkroom_title_part">
        <div ref="talkRoomTitle" class="talkroom_title align-center" data-testid="talkroom-title">
          <AtomsUserIcon
            v-if="selectedTalkRoom.isDirectMessage"
            :width="20"
            :height="20"
            :src="selectedRoomIconSrc"
            :is-owner="!isOwner"
            class="mr-2 talkroom_title_icon"
          />
          <AtomsSvgIcon
            v-else
            :width="20"
            :height="20"
            :img-path="selectedRoomIconSrc"
            class="mr-2 talkroom_title_icon"
          />
          <span class="talkroom_title" :class="{ talkroom_title_canview: canViewSelectedRoom }">{{
            selectedRoomNameLabel
          }}</span>
        </div>
        <div v-if="selectedTalkRoom.description" class="talkroom_desc">
          <span class="talkroom_description_sp">{{ selectedRoomDescriptionLabel }}</span>
        </div>
      </div>
    </template>
    <template v-else>
      <div class="d-flex align-center">
        <AtomsUserIcon
          v-if="selectedTalkRoom.isDirectMessage"
          :width="20"
          :height="20"
          :src="selectedRoomIconSrc"
          :is-owner="!isOwner"
          class="mr-2"
        />
        <AtomsSvgIcon v-else :width="20" :height="20" :img-path="selectedRoomIconSrc" class="mr-2" />
        <span
          ref="talkRoomTitle"
          class="talkroom_title"
          :class="{ talkroom_title_canview: canViewSelectedRoom }"
          data-testid="talkroom-title"
          >{{ selectedRoomNameLabel }}</span
        >
        <span class="talkroom_description">{{ selectedRoomDescriptionLabel }}</span>
      </div>
    </template>
    <div v-if="isTablet" class="talkroom-icons-wrapper_sp">
      <div class="ellipsis-menu-wrapper">
        <AtomsSvgIcon
          class="ellipsis-menu"
          :width="20"
          :height="20"
          :margin-x="4"
          img-path="assets/images/icon/ellipsis-menu.svg"
          data-testid="ellipsis-menu"
        />

        <v-menu activator="parent" transition="slide-y-transition">
          <v-list class="menu-list">
            <v-list-item class="menu-item" @click="clickOpenInNew">
              <v-list-item-title class="d-flex">
                <AtomsSvgIcon class="mr-2" :width="20" :height="20" img-path="assets/images/icon/open-in-new.svg" />
                別タブで開く
              </v-list-item-title>
            </v-list-item>
            <v-list-item class="menu-item" @click="clickSearch">
              <v-list-item-title class="d-flex">
                <AtomsSvgIcon class="mr-2" :width="20" :height="20" img-path="assets/images/icon/talk-search.svg" />
                メッセージを検索
              </v-list-item-title>
            </v-list-item>
            <v-list-item v-if="isOwner" class="menu-item" @click="clickEditTalkRoom">
              <v-list-item-title class="d-flex">
                <AtomsSvgIcon
                  class="mr-2"
                  :width="20"
                  :height="20"
                  img-path="assets/images/icon/setting.svg"
                  data-testid="talkroom-setting"
                />
                {{ selectedTalkRoom.isDirectMessage ? "ダイレクトメッセージ" : "チャンネル" }}の設定
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </div>
    <div v-else class="d-flex talkroom-icons-wrapper">
      <AtomsSvgIcon
        v-if="isOwner"
        class="talkroom-icon-2"
        :width="20"
        :height="20"
        img-path="assets/images/icon/setting.svg"
        data-testid="talkroom-setting"
        @click="clickEditTalkRoom"
      />
      <AtomsSvgIcon
        class="talkroom-icon-1"
        :width="20"
        :height="20"
        :img-path="`assets/images/icon/talk-search${isSearching ? '-active' : ''}.svg`"
        data-testid="talkroom-search"
        @click="clickSearch"
      />
      <AtomsSvgIcon
        class="talkroom-icon-0"
        :width="20"
        :height="20"
        img-path="assets/images/icon/open-in-new.svg"
        @click="clickOpenInNew"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
  @import "assets/media.scss";

  .talkroom_title_wrapper {
    padding: 10px 20px;
    border-bottom: 1px solid rgb(var(--v-theme-monochrome-08));
    color: #8a8a8a;
    .back-button-sp {
      cursor: pointer;
    }
    .talkroom_title_canview {
      color: white;
    }
    .talkroom_title {
      vertical-align: middle;
      cursor: pointer;
    }
    .talkroom_description {
      margin-left: 30px;
      font-size: 14px;
      color: #aaaaaa;
      vertical-align: bottom;
    }
    .talkroom_description_sp {
      font-size: 14px;
      color: #aaaaaa;
      vertical-align: bottom;
    }
    .talkroom-icons-wrapper {
      width: 80px;
    }
    .talkroom-icons-wrapper_sp {
      width: 48px;
    }
    .talkroom-icon-0 {
      position: absolute;
      cursor: pointer;
      top: 15px;
      right: 10px;
    }
    .talkroom-icon-1 {
      position: absolute;
      cursor: pointer;
      top: 15px;
      right: 48px;
    }
    .talkroom-icon-2 {
      position: absolute;
      cursor: pointer;
      top: 15px;
      right: 86px;
    }
  }
  @include display_tablet {
    .talkroom_title_wrapper {
      display: flex;
      justify-content: space-between;
      align-items: center;
      .talkroom_title_part {
        .talkroom_title_icon {
          display: inline-block;
          vertical-align: sub;
        }
        .talkroom_title {
          text-align: center;
          vertical-align: unset;
        }
      }
      .talkroom_desc {
        color: white;
        text-align: center;
      }
    }
  }
  .ellipsis-menu-wrapper {
    height: 40px;
    cursor: pointer;
    padding-top: 10px;
  }
  .ellipsis-menu {
    margin-left: auto;
  }
  .menu-list {
    width: 100vw;
    margin-top: 14px;
    margin-left: -12px;
  }
  .menu-item {
    cursor: pointer;
    background-color: rgba(50, 50, 50, 1);
    border-top: solid 1px rgba(255, 255, 255, 0.1);
    border-bottom: solid 1px rgba(255, 255, 255, 0.1);
  }
</style>
